import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";

import { getCookieAccepted, hasAccessToken } from "@/account/user";
import { Gender, WbOfferStatus } from "@/autoGeneratedGlobalTypes";
import { BalanceType, SuccessPaymentCallbackType, UserContextProps } from "@/contexts/User/types";
import UserContext from "@/contexts/User/UserContext";
import { pollingGetPaymentState_getPayment } from "@/hooks/usePaymentPolling/graphql/__generated__/pollingGetPaymentState";
import MarketingUtils from "@/marketing/marketingUtils";
import { isNullOrUndefined } from "@/utils/commonUtils";

const UserContextProvider = ({ children }: UserContextProps) => {
  const [isUserLoggedIn, setIsUserLoggedIn] = useState<boolean>(false);
  const [isAuthChecked, setIsAuthChecked] = useState<boolean>(false);
  const [userID, setUserID] = useState<number | null>(null);
  const [UUID, setUUID] = useState<string | null>(null);
  const [balance, setBalance] = useState<BalanceType | null>(null);
  const [freeMinutesCount, setFreeMinutesCount] = useState<number | null>(null);
  const [wbOfferStatus, setWbOfferStatus] = useState<WbOfferStatus | null>(null);
  const [wbOfferMinutesAmount, setWbOfferMinutesAmount] = useState<number | null>(null);
  const [wbOfferPrice, setWbOfferPrice] = useState<number | null>(null);
  const [wbOfferProductId, setWbOfferProductId] = useState<number | null>(null);
  const [unfinishedPaymentId, setUnfinishedPaymentId] = useState<number | null>(null);
  const [email, setEmail] = useState<string | null>(null); // todo: remove initial null in other places too
  const [phone, setPhone] = useState<string | null>(null);
  const [tmpPhone, setTmpPhone] = useState<string | null>(null);
  const [name, setName] = useState<string | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState<string | null>(null);
  const [anonymous, setAnonymous] = useState<boolean>(false);
  const [gender, setGender] = useState<Gender | null>(null);
  const [timezone, setTimezone] = useState<string | null>(null);
  const [isEmailVerified, setIsEmailVerified] = useState<boolean | null>(false);
  const [promoUnauthorizedUser, setPromoUnauthorizedUser] = useState<string | null>(null);
  const [clientReferrer, setClientReferrer] = useState<string>("");
  const [userCategoryName, setUserCategoryName] = useState<string>("");
  const [fixedPaymentInfo, setFixedPaymentInfo] = useState<{
    isFixPayment: boolean;
    productId: string;
  }>({
    isFixPayment: false,
    productId: "",
  });
  const [currentPaymentData, setCurrentPaymentData] = useState<pollingGetPaymentState_getPayment | null>(null);
  const [isLobbyEnabled, setIsLobbyEnabled] = useState<boolean>(false);
  const [successPaymentCallback, setSuccessPaymentCallback] = useState<SuccessPaymentCallbackType>(
    () =>
      () => {},
  );
  const [failPaymentCallback, setFailPaymentCallback] = useState<SuccessPaymentCallbackType>(
    () =>
      () => {},
  );
  const [isCookiePermitted, setIsCookiePermitted] = useState<null | boolean>(null);

  // Сервер не знает, вошёл ли пользователь, поэтому первая отрисовка в браузере тоже идёт как у гостя,
  // иначе разметка при гидрации не совпадёт. localStorage читаем сразу после неё, ещё до показа страницы.
  // Эффекты дочерних компонентов срабатывают раньше этого, поэтому решения «не вошёл — уводим со страницы»
  // ждут `isAuthChecked`
  useLayoutEffect(() => {
    const cookie = getCookieAccepted();

    setIsUserLoggedIn(hasAccessToken());
    setIsCookiePermitted(isNullOrUndefined(cookie) ? null : cookie === "true");
    setIsAuthChecked(true);
  }, []);

  // Передаём userID в параметры визита Метрики, как только приложение узнало,
  // кто вошёл. Провайдер обёрнут вокруг всего сайта, поэтому это работает
  // на любой странице, включая статические. Для неавторизованных
  // (userID === null) отправки не происходит — проверка внутри метода.
  useEffect(() => {
    MarketingUtils.sendYandexMetricsVisitUserID(userID);
  }, [userID]);

  const UserContextValue = useMemo(
    () => {
      const isWbOfferAvailable = wbOfferStatus === WbOfferStatus.AVAILABLE;
      const isWbOfferActive = wbOfferStatus === WbOfferStatus.ACTIVE;

      return ({
        isUserLoggedIn,
        setIsUserLoggedIn,
        isAuthChecked,
        userID,
        setUserID,
        UUID,
        setUUID,
        balance,
        setBalance,
        freeMinutesCount,
        setFreeMinutesCount,
        wbOfferStatus,
        setWbOfferStatus,
        wbOfferMinutesAmount,
        setWbOfferMinutesAmount,
        wbOfferPrice,
        setWbOfferPrice,
        wbOfferProductId,
        setWbOfferProductId,
        isWbOfferAvailable,
        isWbOfferActive,
        shouldShowWbOffer: isWbOfferAvailable || isWbOfferActive,
        unfinishedPaymentId,
        setUnfinishedPaymentId,
        email,
        setEmail,
        phone,
        setPhone,
        tmpPhone,
        setTmpPhone,
        isEmailVerified,
        setIsEmailVerified,
        name,
        setName,
        dateOfBirth,
        setDateOfBirth,
        anonymous,
        setAnonymous,
        gender,
        setGender,
        timezone,
        setTimezone,
        promoUnauthorizedUser,
        setPromoUnauthorizedUser,
        clientReferrer,
        setClientReferrer,
        userCategoryName,
        setUserCategoryName,
        fixedPaymentInfo,
        setFixedPaymentInfo,
        currentPaymentData,
        setCurrentPaymentData,
        successPaymentCallback,
        setSuccessPaymentCallback,
        failPaymentCallback,
        setFailPaymentCallback,
        isCookiePermitted,
        setIsCookiePermitted,
        isLobbyEnabled,
        setIsLobbyEnabled,
      });
    },
    [
      UUID,
      anonymous,
      balance,
      clientReferrer,
      userCategoryName,
      dateOfBirth,
      email,
      freeMinutesCount,
      wbOfferStatus,
      wbOfferMinutesAmount,
      wbOfferPrice,
      wbOfferProductId,
      gender,
      isEmailVerified,
      fixedPaymentInfo,
      isUserLoggedIn,
      isAuthChecked,
      name,
      phone,
      promoUnauthorizedUser,
      successPaymentCallback,
      failPaymentCallback,
      currentPaymentData,
      timezone,
      tmpPhone,
      unfinishedPaymentId,
      userID,
      isCookiePermitted,
      setIsCookiePermitted,
      isLobbyEnabled,
    ],
  );

  return <UserContext.Provider value={UserContextValue}>{children}</UserContext.Provider>;
};

export default UserContextProvider;
